import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {useEffect, useState, useMemo, useCallback} from "react";
import {observer} from "mobx-react";
import moment from "moment";
import {DataTable} from "components/dataTable/index.jsx";
import {DateTimeFilter} from "components/dataTable/columnFilter.jsx";
import formatters from "helpers/formatters.jsx";
import Products from "./products";
import DisneyBalance from "./balance";

export default observer(function Disney(props) {
  const appDataStore = useAppDataStore();
  const [activeTab, setActiveTab] = useState('sales');
  const [summary, setSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showNoveltyModal, setShowNoveltyModal] = useState(false);
  const [noveltyModalMode, setNoveltyModalMode] = useState('create'); // 'create' o 'view'
  const [existingNovelty, setExistingNovelty] = useState(null);
  const [noveltyForm, setNoveltyForm] = useState({
    type: '',
    description: '',
    evidence: null
  });
  const [reportingNovelty, setReportingNovelty] = useState(false);
  const [showResendEmailModal, setShowResendEmailModal] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Definir funciones primero antes del useMemo
  const handleResendEmail = useCallback(async (sale) => {
    setSelectedSale(sale);
    setResendEmail(sale.email || '');
    setShowResendEmailModal(true);
  }, []);

  const submitResendEmail = async () => {
    try {
      if (!resendEmail || !resendEmail.trim()) {
        alert('Por favor ingresa un correo electrónico');
        return;
      }

      setSendingEmail(true);
      await appDataStore.post("/sales/resend-email", {
        saleId: selectedSale.id,
        alternativeMail: resendEmail.trim()
      });

      alert('Correo reenviado exitosamente');
      setShowResendEmailModal(false);
      setResendEmail('');
      setSelectedSale(null);
    } catch (err) {
      console.error("Error al reenviar correo:", err);
      alert("Error al reenviar correo: " + err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleReportNovelty = useCallback(async (sale) => {
    setSelectedSale(sale);

    if (sale.novelty) {
      // Modo view: obtener el reporte existente
      try {
        const response = await appDataStore.post("/sales/get-novelty", {
          saleId: sale.id
        });
        setExistingNovelty(response.novelty);
        setNoveltyModalMode('view');
      } catch (err) {
        console.error("Error al obtener novedad:", err);
        alert("Error al obtener el reporte de novedad");
        return;
      }
    } else {
      // Modo create: formulario vacío para reportar
      setNoveltyForm({type: '', description: '', evidence: null});
      setNoveltyModalMode('create');
      setExistingNovelty(null);
    }

    setShowNoveltyModal(true);
  }, [appDataStore]);

  const handleNoveltyFormChange = (e) => {
    const {name, value} = e.target;
    setNoveltyForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEvidenceChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setNoveltyForm(prev => ({
          ...prev,
          evidence: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const submitNovelty = async () => {
    try {
      if (!noveltyForm.type || !noveltyForm.description.trim()) {
        alert('Por favor completa todos los campos');
        return;
      }

      setReportingNovelty(true);
      await appDataStore.post("/sales/report-novelty", {
        saleId: selectedSale.id,
        noveltyType: noveltyForm.type,
        description: noveltyForm.description.trim(),
        evidence: noveltyForm.evidence
      });

      alert('Novedad reportada exitosamente');
      setShowNoveltyModal(false);
      setNoveltyForm({type: '', description: '', evidence: null});
      setSelectedSale(null);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Error al reportar novedad:", err);
      alert("Error al reportar novedad: " + err.message);
    } finally {
      setReportingNovelty(false);
    }
  };

  // Filtro personalizado para Estado - basado en StatusFilter pero con valores de Disney
  const DisneyStatusFilter = ({column}) => {
    const [value, setValue] = useState('');

    useEffect(() => {
      if (value) {
        column.setFilterValue([{key: column.id, operator: 'eq', value}]);
      }
      if (!value) {
        column.setFilterValue([]);
      }
    }, [value, column]);

    return (
      <select
        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="">Todas</option>
        <option value="success">Exitosa</option>
        <option value="pending">Pendiente</option>
        <option value="error">Fallida</option>
      </select>
    );
  };

  const NoveltyFilter = ({column}) => {
    const [value, setValue] = useState('');
    useEffect(() => {
      if (value === '1') column.setFilterValue([{key: 'novelty', operator: 'eq', value: 1}]);
      else if (value === '0') column.setFilterValue([{key: 'novelty', operator: 'eq', value: 0}]);
      else column.setFilterValue([]);
    }, [value]);
    return (
      <select
        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      >
        <option value="">Todas</option>
        <option value="1">Con novedad</option>
        <option value="0">Sin novedad</option>
      </select>
    );
  };

  const columns = useMemo(() => ([
    {
      accessorKey: 'transactionId',
      header: 'ID',
    },
    {
      accessorKey: 'reference',
      header: 'Referencia',
    },
    {
      accessorKey: 'customerName',
      header: 'Cliente',
    },
    {
      accessorKey: 'balanceBefore',
      header: 'Saldo anterior',
      cell: ({cell}) => formatters.cash(cell.getValue()),
    },
    {
      accessorKey: 'balanceAfter',
      header: 'Saldo final',
      cell: ({cell}) => formatters.cash(cell.getValue()),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({cell}) => {
        const email = cell.getValue();
        return email ? email.substring(0, 25) + (email.length > 25 ? "..." : "") : "-";
      }
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({cell}) => formatters.cash(cell.getValue()),
    },
    {
      accessorKey: 'product',
      header: 'Producto',
      opFilter: 'eq',
    },
    {
      accessorKey: 'result',
      header: 'Estado',
      opFilter: 'eq',
      cell: ({cell}) => {
        const result = cell.getValue();
        const statusClass = result === "success" ? "bg-green-100 text-green-800" :
          result === "pending" ? "bg-yellow-100 text-yellow-800" :
            result === "error" ? "bg-red-100 text-red-800" :
              "bg-gray-100 text-gray-800";
        return <span className={`px-2 py-1 rounded text-xs font-medium ${statusClass}`}>{result}</span>;
      },
      customFilter: (column) => <DisneyStatusFilter column={column}/>
    },
    {
      accessorKey: 'createdAt',
      header: 'Fecha/Hora',
      customFilter: (column, table) => <DateTimeFilter column={column} table={table}/>,
      cell: ({cell}) => moment(cell.getValue()).format('YYYY-MM-DD HH:mm:ss'),
      exportFormat: (row) => moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      accessorKey: 'novelty',
      header: 'Novedad',
      enableColumnFilter: true,
      cell: ({row, cell}) => {
        if (!cell.getValue()) {
          return (
            <button
              onClick={() => handleReportNovelty(row.original)}
              className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium hover:bg-gray-200 border border-gray-300 whitespace-nowrap"
            >
              Reportar
            </button>
          );
        }
        const status = row.original.SalesNovelty?.status;
        if (status === 'approved') return <button onClick={() => handleReportNovelty(row.original)} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium hover:bg-green-200 border border-green-300 whitespace-nowrap">✓ Aprobada</button>;
        if (status === 'rejected') return <button onClick={() => handleReportNovelty(row.original)} className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium hover:bg-red-200 border border-red-300 whitespace-nowrap">✗ Rechazada</button>;
        return <button onClick={() => handleReportNovelty(row.original)} className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium hover:bg-yellow-200 border border-yellow-300 whitespace-nowrap">⚠ Pendiente</button>;
      },
      exportFormat: (row) => row.novelty ? 'Sí' : 'No',
      customFilter: (column) => <NoveltyFilter column={column}/>
    },
    {
      id: 'actions',
      header: 'Acciones',
      enableColumnFilter: false,
      cell: ({row}) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleResendEmail(row.original)}
            className="px-3 py-1 rounded text-xs font-medium bg-green-600 text-white hover:bg-green-700"
          >
            Reenviar Correo
          </button>
        </div>
      )
    }
  ]), [handleReportNovelty, handleResendEmail]);

  const tabs = [
    {id: 'sales', label: 'Ventas', icon: '📊'},
    {id: 'products', label: 'Productos', icon: '📦'},
    {id: 'balance', label: 'Mi Saldo', icon: '💰'}
  ];

  // Cargar resumen y refrescar tabla al cambiar de tab
  useEffect(() => {
    if (activeTab === 'sales') {
      loadSummary();
      setRefreshKey(prev => prev + 1);
    }
  }, [activeTab]);

  const loadSummary = async () => {
    try {
      const data = await appDataStore.post("/sales/summary", {});
      setSummary(data);
    } catch (err) {
      console.error("Error al cargar resumen:", err);
    }
  };

  const dataFetch = useCallback(async (
    records,
    currentPage,
    order,
    search,
    filter
  ) => {
    try {
      const requestData = {
        records,
        currentPage,
        order,
        search,
        filter,
        attributes: ['id', 'amount', 'balanceBefore', 'balanceAfter', 'result', 'error', 'transactionId', 'reference', 'phoneNumber', 'email', 'customerName', 'novelty', 'product', 'createdAt'],
        searchAttributes: ['transactionId', 'reference', 'customerName', 'email', 'phoneNumber'],
        include: [{model: 'SalesNovelty', attributes: ['status'], required: false}]
      };

      const response = await appDataStore.post("/app/datasource/Sale", requestData);

      return {
        data: response.data || [],
        totalRows: response.totalRows || 0,
        totalPages: response.totalPages || 1
      };
    } catch (err) {
      console.error("Error al cargar ventas:", err);
      return {
        data: [],
        totalRows: 0,
        totalPages: 1
      };
    }
  }, [appDataStore]);

  return (
    <>
      <div className="p-2 text-black">
        {/* Tabs Navigation */}
        <div className="flex border-b border-gray-300 mb-6 gap-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              <span className="text-lg mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="w-full">
          {activeTab === 'sales' && (
            <div className="animate-fadeIn">
              {/* Resumen de Ventas */}
              {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap- mb-6">
                  <div className="border border-gray-300 rounded-lg p-4 m-2 bg-blue-50">
                    <p className="text-sm text-gray-600">Total de Ventas</p>
                    <p className="text-3xl font-bold text-blue-600">{summary.totalSales || 0}</p>
                  </div>
                  <div className="border border-gray-300 rounded-lg p-4 m-2 bg-green-50">
                    <p className="text-sm text-gray-600">Valor ventas</p>
                    <p className="text-3xl font-bold text-green-600">{formatters.cash(summary.totalRevenue || 0)}</p>
                  </div>

                </div>
              )}

              {/* DataTable de Ventas */}
              <DataTable
                exportFileName={`ventas_disney_${moment().format('DDMMYY_HHmm')}`}
                columns={columns}
                dataFetch={dataFetch}
                defaultSorting={[{id: 'createdAt', desc: true}]}
                refreshDependencies={refreshKey}
              />
            </div>
          )}

          {activeTab === 'products' && (
            <div className="animate-fadeIn">
              <Products {...props} />
            </div>
          )}

          {activeTab === 'balance' && (
            <div className="animate-fadeIn">
              <DisneyBalance {...props} />
            </div>
          )}
        </div>
      </div>

      {/* Modal Reportar/Ver Novedad */}
      {showNoveltyModal && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Encabezado */}
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {noveltyModalMode === 'view' ? 'Detalle de Novedad Reportada' : 'Reportar Novedad en Venta'}
            </h3>

            {/* Información de la Venta */}
            <div className="mb-4 p-3 bg-gray-100 rounded">
              <p className="text-sm text-gray-600"><strong>ID Transacción:</strong> {selectedSale.transactionId}</p>
              <p className="text-sm text-gray-600"><strong>Referencia:</strong> {selectedSale.reference}</p>
              <p className="text-sm text-gray-600"><strong>Cliente:</strong> {selectedSale.customerName}</p>
              <p className="text-sm text-gray-600"><strong>Monto:</strong> {formatters.cash(selectedSale.amount)}</p>
              <p className="text-sm text-gray-600"><strong>Producto:</strong> {selectedSale.product}</p>
            </div>

            {noveltyModalMode === 'view' && existingNovelty && (
              // Modo VIEW: Mostrar reporte existente
              <div className="space-y-4">
                {/* Tipo de Novedad */}
                <div className="p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-sm text-gray-600 mb-1"><strong>Tipo de Novedad:</strong></p>
                  <p className="text-sm text-gray-800">{existingNovelty.noveltyType}</p>
                </div>

                {/* Descripción */}
                <div className="p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-sm text-gray-600 mb-1"><strong>Descripción:</strong></p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{existingNovelty.description}</p>
                </div>

                {/* Estado */}
                <div className="p-3 bg-blue-50 rounded border border-blue-200">
                  <p className="text-sm text-gray-600 mb-1"><strong>Estado:</strong></p>
                  <p className={`text-sm font-semibold ${
                    existingNovelty.status === 'approved' ? 'text-green-700' :
                      existingNovelty.status === 'rejected' ? 'text-red-700' :
                        'text-yellow-700'
                  }`}>
                    {existingNovelty.status === 'pending' ? 'Pendiente' :
                      existingNovelty.status === 'approved' ? 'Aprobada' :
                        'Rechazada'}
                  </p>
                </div>

                {/* Evidencia */}
                {existingNovelty.evidence && (
                  <div className="p-3 bg-blue-50 rounded border border-blue-200">
                    <p className="text-sm text-gray-600 mb-2"><strong>Evidencia:</strong></p>
                    <a
                      href={existingNovelty.evidence}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      Ver archivo
                    </a>
                  </div>
                )}

                {/* Solución */}
                {existingNovelty.solution && (
                  <div className="p-3 bg-green-50 rounded border border-green-200">
                    <p className="text-sm text-gray-600 mb-1"><strong>Solución Aplicada:</strong></p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{existingNovelty.solution}</p>
                  </div>
                )}

                {/* Razón de Rechazo */}
                {existingNovelty.rejectionReason && (
                  <div className="p-3 bg-red-50 rounded border border-red-200">
                    <p className="text-sm text-gray-600 mb-1"><strong>Motivo del Rechazo:</strong></p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{existingNovelty.rejectionReason}</p>
                  </div>
                )}

                {/* Fechas */}
                <div className="text-xs text-gray-500 p-3 bg-gray-50 rounded">
                  <p>Reportado: {moment(existingNovelty.createdAt).format('YYYY-MM-DD HH:mm:ss')}</p>
                  {existingNovelty.updatedAt && (
                    <p>Actualizado: {moment(existingNovelty.updatedAt).format('YYYY-MM-DD HH:mm:ss')}</p>
                  )}
                </div>
              </div>
            )}

            {noveltyModalMode === 'create' && (
              // Modo CREATE: Formulario para reportar
              <div className="space-y-4">
                {/* Tipo de Novedad */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Novedad *
                  </label>
                  <select
                    name="type"
                    value={noveltyForm.type}
                    onChange={handleNoveltyFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="">Selecciona un tipo</option>
                    <option value="error_monto">Error en Monto</option>
                    <option value="error_saldo">Error en Saldo</option>
                    <option value="cliente_reclamo">Reclamo del Cliente</option>
                    <option value="fallo_procesamiento">Fallo en Procesamiento</option>
                    <option value="transaccion_duplicada">Transacción Duplicada</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción de la Novedad *
                  </label>
                  <textarea
                    name="description"
                    value={noveltyForm.description}
                    onChange={handleNoveltyFormChange}
                    placeholder="Describe el problema o novedad encontrada..."
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm h-24 resize-none"
                  />
                </div>

                {/* Evidencia */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Evidencia (Archivo)
                  </label>
                  <input
                    type="file"
                    onChange={handleEvidenceChange}
                    accept="image/*,.pdf,.doc,.docx"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  {noveltyForm.evidence && (
                    <p className="text-xs text-green-600 mt-2">Archivo seleccionado correctamente</p>
                  )}
                </div>
              </div>
            )}

            {/* Botones */}
            <div className="flex gap-2 mt-6">
              {noveltyModalMode === 'create' && (
                <>
                  <button
                    onClick={submitNovelty}
                    disabled={reportingNovelty || !noveltyForm.type || !noveltyForm.description.trim()}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-400 font-medium"
                  >
                    {reportingNovelty ? 'Reportando...' : 'Reportar Novedad'}
                  </button>
                  <button
                    onClick={() => {
                      setShowNoveltyModal(false);
                      setSelectedSale(null);
                      setNoveltyForm({type: '', description: '', evidence: null});
                    }}
                    disabled={reportingNovelty}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:bg-gray-400 font-medium"
                  >
                    Cancelar
                  </button>
                </>
              )}

              {noveltyModalMode === 'view' && (
                <button
                  onClick={() => {
                    setShowNoveltyModal(false);
                    setSelectedSale(null);
                    setExistingNovelty(null);
                  }}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                >
                  Cerrar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Reenviar Correo */}
      {showResendEmailModal && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Reenviar Correo de Activación
            </h3>

            {/* Información de la Venta */}
            <div className="mb-4 p-3 bg-gray-100 rounded">
              <p className="text-sm text-gray-600"><strong>ID Transacción:</strong> {selectedSale.transactionId}</p>
              <p className="text-sm text-gray-600"><strong>Referencia:</strong> {selectedSale.reference}</p>
              <p className="text-sm text-gray-600"><strong>Cliente:</strong> {selectedSale.customerName}</p>
              <p className="text-sm text-gray-600"><strong>Producto:</strong> {selectedSale.product}</p>
            </div>

            {/* Campo de correo electrónico */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correo Electrónico *
              </label>
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="ejemplo@correo.com"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Por defecto se usa el correo de la venta. Puedes cambiarlo si es necesario.
              </p>
            </div>

            {/* Botones */}
            <div className="flex gap-2">
              <button
                onClick={submitResendEmail}
                disabled={sendingEmail || !resendEmail.trim()}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 font-medium"
              >
                {sendingEmail ? 'Enviando...' : 'Reenviar Correo'}
              </button>
              <button
                onClick={() => {
                  setShowResendEmailModal(false);
                  setSelectedSale(null);
                  setResendEmail('');
                }}
                disabled={sendingEmail}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:bg-gray-400 font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }
      `}</style>
    </>
  );
});
